import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  DocumentTextIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationCircleIcon,
  ArrowRightIcon,
  PlusIcon,
} from '@heroicons/react/24/outline';
import useDocumentStore from '../../store/documentStore';
import useAuthStore from '../../store/authStore';
import MobileDocumentCard from '../../components/mobile/MobileDocumentCard';
import { documentsAPI } from '../../services/api';

export default function MobileHome() {
  const { documents, fetchDocuments, isLoading } = useDocumentStore();
  const { user } = useAuthStore();
  const [stats, setStats] = useState({
    total: 0,
    processed: 0,
    pending: 0,
    failed: 0,
  });

  useEffect(() => {
    fetchDocuments({ page: 1, limit: 6 });
    documentsAPI.stats().then(res => setStats(res.data)).catch(() => {});
  }, [fetchDocuments]);

  const statCards = [
    {
      label: 'Total Documents',
      value: stats.total,
      icon: DocumentTextIcon,
      color: 'from-blue-500 to-blue-600',
    },
    {
      label: 'Processed',
      value: stats.processed,
      icon: CheckCircleIcon,
      color: 'from-emerald-500 to-emerald-600',
    },
    {
      label: 'Pending',
      value: stats.pending,
      icon: ClockIcon,
      color: 'from-amber-500 to-amber-600',
    },
    {
      label: 'Failed',
      value: stats.failed,
      icon: ExclamationCircleIcon,
      color: 'from-red-500 to-red-600',
    },
  ];

  return (
    <div className="p-4 space-y-6">
      {/* Welcome */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-br from-brand-orange to-brand-amber rounded-2xl p-5 text-white"
      >
        <h1 className="text-xl font-bold">
          Welcome back, {user?.username || 'User'}!
        </h1>
        <p className="text-white/80 text-sm mt-1">
          Manage your Arabic documents with AI-powered OCR
        </p>
        <Link
          to="/upload"
          className="inline-flex items-center gap-2 mt-4 px-4 py-2 bg-white/20 hover:bg-white/30 rounded-xl text-sm font-medium transition-colors"
        >
          <PlusIcon className="w-5 h-5" />
          Upload New Document
        </Link>
      </motion.div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 gap-3">
        {statCards.map((stat, index) => (
          <motion.div
            key={stat.label}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="bg-theme-secondary rounded-2xl p-4 border border-theme-primary"
          >
            <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${stat.color} flex items-center justify-center mb-3`}>
              <stat.icon className="w-5 h-5 text-white" />
            </div>
            <p className="text-2xl font-bold text-theme-primary">{stat.value}</p>
            <p className="text-xs text-theme-muted mt-0.5">{stat.label}</p>
          </motion.div>
        ))}
      </div>

      {/* Recent Documents */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-theme-primary">Recent Documents</h2>
          <Link
            to="/documents"
            className="flex items-center gap-1 text-sm text-brand-orange font-medium"
          >
            View All
            <ArrowRightIcon className="w-4 h-4" />
          </Link>
        </div>

        {isLoading ? (
          <div className="grid grid-cols-2 gap-3">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="bg-theme-secondary rounded-2xl overflow-hidden animate-pulse">
                <div className="aspect-[4/3] bg-theme-tertiary" />
                <div className="p-3 space-y-2">
                  <div className="h-4 bg-theme-tertiary rounded w-3/4" />
                  <div className="h-3 bg-theme-tertiary rounded w-1/2" />
                </div>
              </div>
            ))}
          </div>
        ) : documents.length === 0 ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-12 bg-theme-secondary rounded-2xl border border-theme-primary"
          >
            <DocumentTextIcon className="w-16 h-16 mx-auto text-theme-muted mb-4" />
            <h3 className="text-lg font-medium text-theme-primary mb-2">
              No documents yet
            </h3>
            <p className="text-sm text-theme-muted mb-4">
              Upload your first document to get started
            </p>
            <Link
              to="/upload"
              className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-brand-orange to-brand-amber text-white rounded-xl text-sm font-medium"
            >
              <PlusIcon className="w-5 h-5" />
              Upload Document
            </Link>
          </motion.div>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            {documents.slice(0, 6).map((doc, index) => (
              <motion.div
                key={doc.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <MobileDocumentCard document={doc} />
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
